import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { StepAbout, StepGoal, StepSymptoms, StepSchedule, StepFlags, StepHealthData, StepLength, StepReview } from "../components/questionnaire/Steps";

const STEPS = ["About you", "Your goal", "Symptoms", "Schedule", "Health flags", "Health data", "Program length", "Review"];
const BLANK = {
  first_name: "", email: "", age: 35, sex: "", owns_device: "yes", primary_goal: "", symptoms: [], symptom_details: "",
  severity: 5, duration: "months", pain_location: "", minutes_per_day: "as_recommended", preferred_times: [],
  has_autoimmune: false, autoimmune_details: "", medications: "", pregnancy_or_pacemaker: false, lifestyle_notes: "",
  document_ids: [], diagnoses: "", supplements: "", sleep_quality: "", stress_level: "",
  progress_since_last: "", progress_notes: "", previous_plan_id: "", program_length: "one_week", consent_disclaimer: false,
};

export default function Questionnaire() {
  const { user, login, register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const fromId = params.get("from");
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [docs, setDocs] = useState([]);
  const [pending, setPending] = useState([]);
  const [acct, setAcct] = useState({ mode: "register", password: "" });
  const top = useRef(null);
  const [d, setD] = useState(() => {
    const draft = !fromId && JSON.parse(sessionStorage.getItem("wt_q_draft") || "null");
    return draft || { ...BLANK, first_name: user?.name || "", email: user?.email || "",
      owns_device: user?.owns_device ? "yes" : "planning", primary_goal: params.get("goal") || "" };
  });

  const set = (k, v) => setD((p) => ({ ...p, [k]: typeof v === "function" ? v(p[k]) : v }));
  const toggle = (k, v) => setD((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  useEffect(() => {
    if (!user) return;
    setD((p) => ({ ...p, first_name: p.email === user.email && p.first_name ? p.first_name : user.name, email: user.email }));
    api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
  }, [user]);
  useEffect(() => { if (!fromId) sessionStorage.setItem("wt_q_draft", JSON.stringify({ ...d, consent_disclaimer: false })); }, [d, fromId]);
  useEffect(() => {
    if (!fromId || !user) return;
    api.get(`/plans/${fromId}/submission`).then(({ data }) => {
      const keep = Object.fromEntries(Object.keys(BLANK).filter((k) => data[k] !== undefined).map((k) => [k, data[k]]));
      setD((p) => ({ ...p, ...keep, owns_device: user?.owns_device ? "yes" : keep.owns_device || p.owns_device, email: user?.email || p.email, previous_plan_id: fromId, consent_disclaimer: false, progress_since_last: "", progress_notes: "", document_ids: [] }));
      toast.message("We pre-filled your last answers — update anything that changed.", { id: "prefill" });
    }).catch(() => {});
  }, [fromId, user]);
  useEffect(() => { top.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [step]);

  const valid = [
    d.first_name.trim() && d.email.trim() && d.age > 0 && d.age <= 120, !!d.primary_goal, true, !!d.minutes_per_day, true, true, !!d.program_length, d.consent_disclaimer && (!!user || acct.password.length >= 6),
  ][step];

  const submit = async () => {
    if (!d.consent_disclaimer) return toast.error("Please accept the educational-use disclaimer.");
    setBusy(true);
    try {
      if (!user) {
        try {
          if (acct.mode === "register") await register(d.first_name, d.email, acct.password);
          else await login(d.email, acct.password);
        } catch (e) {
          if (acct.mode === "register" && e?.response?.status === 400) {
            setAcct({ ...acct, mode: "login" });
            toast.error("You already have an account with this email — enter your password to sign in.");
          } else toast.error(formatError(e));
          setBusy(false);
          return;
        }
      }
      const ids = [...d.document_ids];
      for (const f of pending) {
        const fd = new FormData();
        fd.append("file", f);
        try { ids.push((await api.post("/documents", fd)).data.id); } catch (e) { toast.error(`${f.name}: ${formatError(e)}`); }
      }
      if (pending.length) { setPending([]); setD((p) => ({ ...p, document_ids: ids })); }
      const { data } = await api.post("/questionnaire/submit", { ...d, document_ids: ids });
      sessionStorage.removeItem("wt_q_draft");
      nav(`/plan/${data.id}`);
    } catch (e) {
      toast.error(formatError(e));
      setBusy(false);
    }
  };

  const props = { d, set, toggle, docs, setDocs, reassess: !!fromId, user, pending, setPending, acct, setAcct };
  const Comp = [StepAbout, StepGoal, StepSymptoms, StepSchedule, StepFlags, StepHealthData, StepLength, StepReview][step];

  return (
    <div className="py-10 md:py-16" data-testid="questionnaire-page">
      <div className="max-w-2xl mx-auto px-5" ref={top}>
        <div className="mb-8">
          {!user && (
            <div data-testid="questionnaire-signin-banner" className="mb-5 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-[#E4DFD4] bg-white px-4 py-3 text-sm">
              <span className="text-slate-600">Answered before? Sign in to see your plans and do your monthly check-in.</span>
              <Link to="/login?next=/dashboard" data-testid="questionnaire-signin-link" className="font-semibold text-[#0D9488] hover:underline">Sign in</Link>
            </div>
          )}
          {fromId && <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D97706] mb-2" data-testid="reassess-badge">Monthly re-assessment</div>}
          <p className="text-xs tracking-[0.2em] uppercase text-[#0D9488] font-semibold mb-3" data-testid="onboarding-step-label">Step {step + 1} of {STEPS.length} — {STEPS[step]}</p>
          <div className="h-1 bg-[#EFECE5] rounded-full overflow-hidden">
            <div data-testid="onboarding-progress" className="h-full bg-[#0D9488] transition-[width] duration-500" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
          </div>
        </div>
        <div key={step} data-testid="onboarding-card" className="fade-up bg-white rounded-3xl p-7 md:p-11 border border-[#E4DFD4] shadow-[0_8px_30px_rgba(0,0,0,0.04)]">
          <Comp {...props} />
          <div className="mt-10 flex items-center justify-between">
            <button type="button" data-testid="onboarding-back" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#0D9488] disabled:opacity-30 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button type="button" data-testid="onboarding-next" disabled={!valid} onClick={() => setStep((s) => s + 1)} className="inline-flex items-center gap-2 bg-[#0D9488] hover:bg-[#0F766E] disabled:opacity-50 text-white font-semibold px-6 py-3 rounded-full transition-colors">
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button type="button" data-testid="onboarding-submit" disabled={!valid || busy} onClick={submit} className="inline-flex items-center gap-2 bg-[#0D9488] hover:bg-[#0F766E] disabled:opacity-50 text-white font-semibold px-6 py-3 rounded-full transition-colors">
                {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Starting…</> : <>Generate my plan <ArrowRight className="w-4 h-4" /></>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
